import type { Page, SuccessPhpResponse } from '@/types';

/**
 * Lead conversion values (EUR) per lead prio, sent as `eventValue` on the GA `submission-success` event and
 * forwarded by GTM to the ad platforms (Google Ads, Meta, ...).
 *
 * Source of truth: Google Sheet "Conversion-Schnittstelle", tab "Conversion Values"
 * (https://docs.google.com/spreadsheets/d/11H4Ml1N0CI5kmWD71mQGT4ASZKZTAHWpzWwnZS4dd5w).
 * Keep these numbers in sync with the sheet.
 *
 * `fallback` is the sheet's "Fallback (keine/unbekannte Prio)" row, used when the response carries no
 * usable prio (e.g. bussgeldcheck's non-RSV / bussgeldinfo exits, which return no Randomizer).
 */
type ProfitCenterValues = { 1: number; 2: number; 3: number; 4: number; fallback: number };

const PROFIT_CENTER_VALUES = {
  'traffic-law': { 1: 189.62, 2: 117.62, 3: 40.05, 4: 2.03, fallback: 58.31 },
  'debt-management': { 1: 96.75, 2: 32.4, 3: 19.5, 4: 4.5, fallback: 39.0 },
  'immigration-law': { 1: 127.5, 2: 77.5, 3: 27.5, 4: 0, fallback: 47.5 },
} satisfies Record<string, ProfitCenterValues>;

type ProfitCenter = keyof typeof PROFIT_CENTER_VALUES;

/** Pages without an entry keep sending an empty `eventValue`. */
const PAGE_TO_PROFIT_CENTER: Partial<Record<Page, ProfitCenter>> = {
  bussgeldcheck: 'traffic-law',
  schuldenanalyse: 'debt-management',
  passexperten: 'immigration-law',
  'passexperten-rightmart': 'immigration-law',
};

/**
 * success.php responses are not uniform across pages:
 * - bussgeldcheck / passexperten / schuldenanalyse leads: `Randomizer`
 * - schuldenanalyse: `Randomizer` is only filled when prioVersion = 2, `RandomizerV2` always carries the prio class
 * - passexperten Braze prospects: only `prio`
 */
const readPrio = (data: SuccessPhpResponse): number | null => {
  const candidates = [data.Randomizer, data.RandomizerV2, data.prio];
  for (const candidate of candidates) {
    const prio = Number(candidate);
    if (candidate !== null && candidate !== undefined && candidate !== '' && [1, 2, 3, 4].includes(prio)) {
      return prio;
    }
  }
  return null;
};

/**
 * Returns the conversion value as a plain number in EUR with dot decimals (e.g. `189.62`), which is
 * what Google Ads (`value`) and Meta (`value`) expect. Returns `''` for pages without a mapping.
 */
export const getConversionValue = (page: Page, data: SuccessPhpResponse): number | '' => {
  const profitCenter = PAGE_TO_PROFIT_CENTER[page];
  if (!profitCenter) return '';

  const values: ProfitCenterValues = PROFIT_CENTER_VALUES[profitCenter];
  const prio = readPrio(data);
  return prio === null ? values.fallback : values[prio as 1 | 2 | 3 | 4];
};
